<!--
  Agenzie — lista.

  1) NIENTE COLONNA "FOGLIO": il foglio per agenzia non ha una struttura a
     schema. La terza KPI mostra invece i driver operativi.

  2) NIENTE COLONNA "AREE": le zone stanno sui driver come
     Regione→Provincia, non sull'agenzia.

  Il numero di driver non arriva con l'agenzia (`drivers` non è fra i join
  consentiti su /agency): si conta a parte, una chiamata piccola per riga.
-->
<template>
  <div class="agn">
    <div class="agn__head">
      <div>
        <h1 class="agn__title">
          Agenzie
        </h1>
        <p class="agn__sub">
          Agenzie che forniscono i driver — anagrafica e tariffe compensi.
        </p>
      </div>
      <div class="agn__headActions">
        <el-input
          v-model="search"
          class="agn__search"
          placeholder="Cerca agenzia, referente, mail…"
          clearable
          @input="onSearchInput"
        />
        <router-link
          to="/add-agency"
          class="routerLink"
        >
          <el-button type="primary">
            Nuova agenzia
          </el-button>
        </router-link>
      </div>
    </div>

    <div class="agn__kpis">
      <div
        v-for="k in kpis"
        :key="k.label"
        class="agn__kpi"
      >
        <span class="agn__kpiNum">{{ k.value === null ? '—' : k.value }}</span>
        <span class="agn__kpiLbl">{{ k.label }}</span>
      </div>
    </div>

    <div class="agn__card">
      <el-table
        v-loading="loading"
        :data="rows"
        style="width: 100%"
        empty-text="Nessuna agenzia corrisponde alla ricerca"
        @row-click="openDetail"
        @sort-change="onSortChange"
      >
        <el-table-column
          label="Agenzia"
          prop="company_name"
          sortable="custom"
          min-width="230"
        >
          <template #default="scope">
            <div class="agn__name">
              {{ scope.row.company_name || '—' }}
            </div>
            <div
              v-if="referente(scope.row)"
              class="agn__ref"
            >
              Ref. {{ referente(scope.row) }}
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Contatti"
          min-width="220"
        >
          <template #default="scope">
            <div>{{ scope.row.mail || '—' }}</div>
            <div class="agn__ref">
              {{ scope.row.telephone_number || '—' }}
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Driver"
          width="100"
          align="right"
        >
          <template #default="scope">
            <span class="agn__ndrv">
              {{ driverCounts[scope.row.id] ?? '…' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
          label="Tariffa oraria"
          width="140"
          align="right"
        >
          <template #default="scope">
            <span class="agn__val">{{ euro(scope.row.hourly_rate) }}/h</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Rimborso km"
          width="140"
          align="right"
        >
          <template #default="scope">
            <span class="agn__val">{{ euro(scope.row.km_refund_rate) }}/km</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Compenso notturno"
          width="160"
          align="right"
        >
          <template #default="scope">
            <span class="agn__val">{{ euro(scope.row.night_compensation_rate) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          width="120"
          align="right"
          label="Azioni"
          fixed="right"
        >
          <template #default="scope">
            <el-button
              size="small"
              type="primary"
              @click.stop="openDetail(scope.row)"
            >
              Dettagli
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="agn__foot">
        <span class="agn__footInfo">
          {{ total }} agenzie
          <template v-if="pageCount > 1">· pagina {{ page }} di {{ pageCount }}</template>
        </span>
        <el-pagination
          background
          layout="prev, pager, next"
          :page-size="pageSize"
          :total="total"
          :current-page="page"
          @current-change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import agencyAPI from '../../services/agencyAPI.js';

const PAGE_SIZE = 25;

export default {
  name: 'AgencyPage',

  setup() {
    const router = useRouter();

    const rows = ref([]);
    const loading = ref(false);
    const page = ref(1);
    const total = ref(0);
    const pageCount = ref(1);
    const search = ref('');
    const sort = ref('');

    // conteggi driver per agenzia, riempiti dopo il caricamento della pagina
    const driverCounts = ref({});
    const totalDrivers = ref(null);
    const activeDrivers = ref(null);

    const kpis = computed(() => [
      { label: 'Agenzie', value: total.value },
      { label: 'Driver totali', value: totalDrivers.value },
      { label: 'Driver operativi', value: activeDrivers.value },
    ]);

    const load = async () => {
      loading.value = true;
      try {
        const res = await agencyAPI.getAgenciesPaged({
          page: page.value,
          limit: PAGE_SIZE,
          search: search.value,
          sort: sort.value,
        });
        rows.value = res.rows;
        total.value = res.total;
        pageCount.value = res.pageCount || 1;
      } catch (e) {
        console.error('Caricamento agenzie fallito:', e);
        rows.value = [];
        total.value = 0;
      } finally {
        loading.value = false;
      }
      loadDriverCounts();
    };

    // Una chiamata di conteggio per riga: `drivers` non è fra i join di
    // /agency. Sono richieste minime (limit=1, si legge solo `total`) e
    // limitate alle 25 righe della pagina, quindi restano parallele e brevi.
    const loadDriverCounts = async () => {
      const ids = rows.value.map((a) => a.id).filter(Boolean);
      if (!ids.length) return;
      const res = await Promise.allSettled(
        ids.map((id) => agencyAPI.countDrivers({ agencyId: id }))
      );
      const next = { ...driverCounts.value };
      res.forEach((r, i) => {
        if (r.status === 'fulfilled') next[ids[i]] = r.value;
      });
      driverCounts.value = next;
    };

    // I totali dei driver sono globali, non per pagina: si chiedono una volta.
    const loadDriverTotals = async () => {
      const [all, active] = await Promise.allSettled([
        agencyAPI.countDrivers(),
        agencyAPI.countDrivers({ onlyOperativi: true }),
      ]);
      if (all.status === 'fulfilled') totalDrivers.value = all.value;
      if (active.status === 'fulfilled') activeDrivers.value = active.value;
    };

    const reload = () => { page.value = 1; load(); };
    const onPageChange = (p) => { page.value = p; load(); };

    const onSortChange = ({ prop, order }) => {
      sort.value = prop && order ? `${prop},${order === 'ascending' ? 'ASC' : 'DESC'}` : '';
      reload();
    };

    let timer = null;
    const onSearchInput = () => {
      clearTimeout(timer);
      timer = setTimeout(reload, 350);
    };

    const openDetail = (row) => {
      if (row?.id) router.push(`/agency/${row.id}`);
    };

    const referente = (a) => [a?.name, a?.surname].filter(Boolean).join(' ');

    const euro = (n) =>
      n == null || n === '' ? '—' : `€ ${Number(n).toFixed(2).replace('.', ',')}`;

    onMounted(() => { load(); loadDriverTotals(); });

    return {
      rows, loading, page, total, pageCount, search, kpis, driverCounts,
      pageSize: PAGE_SIZE,
      onPageChange, onSortChange, onSearchInput, openDetail, referente, euro,
    };
  },
};
</script>

<style scoped>
.agn { padding: 4px 0 32px; }

.agn__head {
  display: flex; align-items: flex-end; justify-content: space-between;
  gap: 16px; flex-wrap: wrap; margin-bottom: 18px;
}
.agn__title { margin: 0; font-size: 22px; color: var(--text-primary); }
.agn__sub { margin: 4px 0 0; font-size: 13px; color: var(--text-tertiary); }
.agn__headActions { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
.agn__search { width: 280px; }

.agn__kpis {
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px; margin-bottom: 16px;
}
@media (max-width: 700px) { .agn__kpis { grid-template-columns: 1fr; } }
.agn__kpi {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); padding: 13px 15px;
}
.agn__kpiNum {
  display: block; font-size: 24px; font-weight: 750; letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums; color: var(--text-primary);
}
.agn__kpiLbl {
  display: block; margin-top: 2px; font-size: 11.5px; font-weight: 600;
  letter-spacing: 0.06em; text-transform: uppercase; color: var(--text-tertiary);
}

.agn__card {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); overflow: hidden;
}
.agn__name { font-weight: 600; color: var(--text-primary); }
.agn__ref { font-size: 12px; color: var(--text-tertiary); }
.agn__ndrv { font-variant-numeric: tabular-nums; font-weight: 700; }
.agn__val { font-variant-numeric: tabular-nums; font-weight: 650; }

.agn__foot {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap; padding: 12px 14px;
  border-top: 1px solid var(--border-color);
}
.agn__footInfo { font-size: 12.5px; color: var(--text-tertiary); }

.agn :deep(.el-table__row) { cursor: pointer; }
/* header chiaro: l'override globale impone un gradiente viola con !important */
.agn :deep(.el-table th.el-table__cell) {
  background: var(--bg-tertiary) !important;
  color: var(--text-tertiary) !important;
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
  padding: 11px 12px; border-bottom: 1px solid var(--border-color);
}
</style>
